import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const SavedTours = ({ savedTours, onRemoveFromWishlist }) => {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('date-added');
  const [filterBy, setFilterBy] = useState('all');

  const sortOptions = [
    { value: 'date-added', label: 'Recently Added' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'popularity', label: 'Most Popular' }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Tours' },
    { value: 'available', label: 'Available' },
    { value: 'price-drop', label: 'Price Dropped' },
    { value: 'limited-time', label: 'Limited Time Offers' }
  ];

  const filteredAndSortedTours = savedTours?.filter(tour => {
      if (filterBy === 'all') return true;
      if (filterBy === 'available') return tour?.availability > 0;
      if (filterBy === 'price-drop') return tour?.priceDropped;
      if (filterBy === 'limited-time') return tour?.limitedOffer;
      return true;
    })?.sort((a, b) => {
      switch (sortBy) {
        case 'date-added':
          return new Date(b.savedDate) - new Date(a.savedDate);
        case 'price-low':
          return a?.price - b?.price;
        case 'price-high':
          return b?.price - a?.price;
        case 'rating':
          return b?.rating - a?.rating;
        case 'popularity':
          return b?.bookings - a?.bookings;
        default:
          return 0;
      }
    });

  const handleViewTour = (tourId) => {
    navigate(`/tour-detail?id=${tourId}`);
  };

  const handleBookNow = (tourId) => {
    navigate(`/tour-detail?id=${tourId}&book=true`);
  };

  const renderRating = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={12}
            className={star <= rating ? 'text-secondary fill-current' : 'text-muted-foreground'}
          />
        ))}
        <span className="text-xs text-muted-foreground ml-1">({rating})</span>
      </div>
    );
  };

  if (savedTours?.length === 0) {
    return (
      <div className="bg-card rounded-xl p-8 border border-border text-center">
        <Icon name="Heart" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Saved Tours</h3>
        <p className="text-muted-foreground mb-4">Save tours you're interested in to view them later</p>
        <Button onClick={() => navigate('/homepage')} iconName="Search" iconPosition="left">
          Discover Tours
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Select
          options={sortOptions}
          value={sortBy}
          onChange={setSortBy}
          placeholder="Sort by"
          className="sm:w-48"
        />
        <Select
          options={filterOptions}
          value={filterBy}
          onChange={setFilterBy}
          placeholder="Filter by"
          className="sm:w-48"
        />
      </div>
      {/* Tours Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAndSortedTours?.map((tour) => (
          <div key={tour?.id} className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-shadow group">
            {/* Tour Image */}
            <div className="relative h-48 overflow-hidden">
              <Image
                src={tour?.image}
                alt={tour?.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              
              {/* Badges */}
              <div className="absolute top-3 left-3 flex flex-col space-y-2">
                {tour?.priceDropped && (
                  <span className="bg-error text-white px-2 py-1 rounded-full text-xs font-medium">
                    Price Drop!
                  </span>
                )}
                {tour?.limitedOffer && (
                  <span className="bg-warning text-white px-2 py-1 rounded-full text-xs font-medium">
                    Limited Time
                  </span>
                )}
              </div>

              {/* Remove from Wishlist */}
              <button
                onClick={() => onRemoveFromWishlist(tour?.id)}
                className="absolute top-3 right-3 p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
              >
                <Icon name="Heart" size={16} className="text-error fill-current" />
              </button>

              {/* Availability */}
              <div className="absolute bottom-3 left-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  tour?.availability > 0 
                    ? 'bg-success text-white' :'bg-error text-white'
                }`}>
                  {tour?.availability > 0 ? `${tour?.availability} spots left` : 'Sold Out'}
                </span>
              </div>
            </div>

            {/* Tour Details */}
            <div className="p-4">
              <div className="mb-2">
                <h3 className="font-semibold text-foreground mb-1 line-clamp-2">{tour?.title}</h3>
                <div className="flex items-center text-muted-foreground mb-2">
                  <Icon name="MapPin" size={14} className="mr-1" />
                  <span className="text-sm">{tour?.location}</span>
                </div>
              </div>

              {/* Rating */}
              <div className="mb-3">
                {renderRating(tour?.rating)}
              </div>

              {/* Price */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  {tour?.originalPrice && tour?.originalPrice > tour?.price && (
                    <span className="text-sm text-muted-foreground line-through">
                      ${tour?.originalPrice}
                    </span>
                  )}
                  <span className="text-lg font-bold text-primary">
                    ${tour?.price}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">per person</span>
              </div>

              {/* Duration */}
              <div className="flex items-center text-muted-foreground mb-4">
                <Icon name="Clock" size={14} className="mr-1" />
                <span className="text-sm">{tour?.duration}</span>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewTour(tour?.id)}
                  className="flex-1"
                >
                  View Details
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleBookNow(tour?.id)}
                  disabled={tour?.availability === 0}
                  className="flex-1"
                >
                  Book Now
                </Button>
              </div>

              {/* Saved Date */}
              <div className="mt-3 pt-3 border-t border-border">
                <span className="text-xs text-muted-foreground">
                  Saved on {new Date(tour.savedDate)?.toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredAndSortedTours?.length === 0 && (
        <div className="bg-card rounded-xl p-8 border border-border text-center">
          <Icon name="Filter" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No tours match your filters</h3>
          <p className="text-muted-foreground">Try adjusting your filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default SavedTours;